import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';
import { toast } from 'sonner';

// Fix Leaflet icons for production builds
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const TYPE_COLOR  = { restaurant: '#D97706', space: '#0D9488', contact: '#2563EB' };
const TYPE_ICON   = { restaurant: '🍽', space: '🏢', contact: '👤' };

const makePin = (color, highlight = false) => L.divIcon({
  html: `<div style="
    width:${highlight?34:26}px;height:${highlight?34:26}px;
    border-radius:50% 50% 50% 0;
    background:${color};
    border:${highlight?'3px solid white':'2px solid rgba(255,255,255,0.8)'};
    box-shadow:0 ${highlight?4:2}px ${highlight?14:8}px rgba(0,0,0,${highlight?.4:.25});
    transform:rotate(-45deg);
    transition:all .15s;
  "></div>`,
  className: '',
  iconSize:   [highlight ? 34 : 26, highlight ? 34 : 26],
  iconAnchor: [highlight ? 17 : 13, highlight ? 34 : 26],
  popupAnchor:[0, highlight ? -36 : -28],
});

const HAMPTONS_CENTER = [40.96, -72.28];

const fmt = (n) => n != null ? `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : null;

// ── Selection tool component ──
function SelectionTool({ mode, onSelectComplete }) {
  const map = useMap();
  const drawing = useRef(false);
  const startLatLng = useRef(null);
  const rect = useRef(null);

  useEffect(() => {
    if (mode !== 'select') {
      map.dragging.enable();
      if (rect.current) { map.removeLayer(rect.current); rect.current = null; }
      return;
    }
    map.dragging.disable();
    const container = map.getContainer();
    container.style.cursor = 'crosshair';

    const onDown = (e) => {
      drawing.current = true;
      startLatLng.current = map.containerPointToLatLng([e.offsetX, e.offsetY]);
      if (rect.current) map.removeLayer(rect.current);
      rect.current = L.rectangle([startLatLng.current, startLatLng.current], {
        color: '#0891b2', weight: 2, fillColor: '#06b6d4', fillOpacity: 0.15, dashArray: '6,4'
      }).addTo(map);
    };
    const onMove = (e) => {
      if (!drawing.current || !rect.current) return;
      const cur = map.containerPointToLatLng([e.offsetX, e.offsetY]);
      rect.current.setBounds([startLatLng.current, cur]);
    };
    const onUp = (e) => {
      if (!drawing.current) return;
      drawing.current = false;
      const end = map.containerPointToLatLng([e.offsetX, e.offsetY]);
      const bounds = L.latLngBounds(startLatLng.current, end);
      onSelectComplete(bounds);
      container.style.cursor = '';
      map.dragging.enable();
    };

    container.addEventListener('mousedown', onDown);
    container.addEventListener('mousemove', onMove);
    container.addEventListener('mouseup', onUp);
    return () => {
      container.removeEventListener('mousedown', onDown);
      container.removeEventListener('mousemove', onMove);
      container.removeEventListener('mouseup', onUp);
      container.style.cursor = '';
      map.dragging.enable();
    };
  }, [mode, map, onSelectComplete]);

  return null;
}

// ── Fly helper ──
function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target.coords, target.zoom || 14, { duration: 1 });
  }, [target, map]);
  return null;
}

// ── Pin popup ──
function PinCard({ pin }) {
  const bg = TYPE_COLOR[pin.type] || '#374151';
  return (
    <div style={{ minWidth: 230, maxWidth: 290, fontFamily: 'Manrope,sans-serif' }}>
      <div style={{ background: bg, padding: '8px 12px', margin: '-8px -12px 10px', borderRadius: '8px 8px 0 0' }}>
        <div style={{ color:'white', fontWeight:700, fontSize:13, lineHeight:1.3 }}>{pin.name}</div>
        <div style={{ color:'rgba(255,255,255,.75)', fontSize:11 }}>{pin.village}{pin.address ? ` · ${pin.address}` : ''}</div>
      </div>
      {pin.type === 'restaurant' && <>
        {pin.cuisine  && <div style={{fontSize:12,color:'#6B7280',marginBottom:3}}>{pin.cuisine}</div>}
        {pin.owner_name && <div style={{fontSize:12}}><b>Owner:</b> {pin.owner_name}</div>}
        {pin.monthly_revenue && (
          <div style={{fontSize:12,background:'#FEF3C7',padding:'4px 8px',borderRadius:6,marginTop:4}}>
            <b>Monthly Rev:</b> {fmt(pin.monthly_revenue)}
            {pin.financial_health && <span style={{marginLeft:6,fontWeight:700,color:pin.financial_health==='Strong'?'#059669':'#D97706'}}>{pin.financial_health}</span>}
          </div>
        )}
      </>}
      {pin.type === 'space' && <>
        {pin.space_type && <div style={{fontSize:12,color:'#6B7280'}}>{pin.space_type}</div>}
        {pin.size_sqft && <div style={{fontSize:12}}><b>Size:</b> {pin.size_sqft.toLocaleString()} sqft</div>}
        {pin.rent_monthly && <div style={{fontSize:12,background:'#CCFBF1',padding:'4px 8px',borderRadius:6,marginTop:4}}><b>Rent:</b> {fmt(pin.rent_monthly)}/mo</div>}
        {pin.landlord_name && <div style={{fontSize:12}}><b>Landlord:</b> {pin.landlord_name}</div>}
      </>}
      {pin.type === 'contact' && <>
        {pin.category && <div style={{fontSize:12,color:'#6B7280'}}>{pin.category}</div>}
        {pin.restaurant_name && <div style={{fontSize:12}}><b>Company:</b> {pin.restaurant_name}</div>}
      </>}
      {(pin.phone || pin.email) && (
        <div style={{marginTop:8,paddingTop:8,borderTop:'1px solid #E5E7EB'}}>
          {pin.phone && <a href={`tel:${pin.phone}`} style={{display:'block',fontSize:12,color:'#2563EB',marginBottom:2}}>📞 {pin.phone}</a>}
          {pin.email && <a href={`mailto:${pin.email}`} style={{display:'block',fontSize:12,color:'#2563EB'}}>✉️ {pin.email}</a>}
        </div>
      )}
      {pin.notes && <div style={{fontSize:11,color:'#9CA3AF',marginTop:6,borderTop:'1px solid #E5E7EB',paddingTop:6}}>{pin.notes}</div>}
      <div style={{display:'flex',gap:6,marginTop:10}}>
        <a href={`/${pin.type==='restaurant'?'restaurants':pin.type==='space'?'spaces':'contacts'}`}
          style={{flex:1,background:bg,color:'white',padding:'6px 0',borderRadius:6,fontSize:12,fontWeight:700,textAlign:'center',textDecoration:'none'}}>
          View in CRM →
        </a>
        {pin.listing_url && <a href={pin.listing_url} target="_blank" rel="noopener noreferrer" style={{background:'#F3F4F6',padding:'6px 10px',borderRadius:6,fontSize:12,textDecoration:'none'}}>🔗</a>}
      </div>
    </div>
  );
}

export default function HamptonsMap() {
  const [pins,       setPins]       = useState([]);
  const [summary,    setSummary]    = useState({});
  const [loading,    setLoading]    = useState(true);
  const [pulling,    setPulling]    = useState(false);
  const [search,     setSearch]     = useState('');
  const [flyTarget,  setFlyTarget]  = useState(null);
  const [mode,       setMode]       = useState('click'); // click | select
  const [selected,   setSelected]   = useState([]);      // selected pins
  const [selBounds,  setSelBounds]  = useState(null);
  const [filters,    setFilters]    = useState({ restaurants: true, spaces: true, contacts: true });
  const [mapStyle,   setMapStyle]   = useState('satellite'); // satellite | street
  const [sidebarTab, setSidebarTab] = useState('browse');   // browse | selected

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const types = Object.entries(filters).filter(([,v])=>v).map(([k])=>k).join(',');
      const [pRes, sRes] = await Promise.all([
        api.get(`/api/map/data?types=${types}`),
        api.get('/api/map/summary'),
      ]);
      setPins(pRes.data.pins || []);
      setSummary(sRes.data || {});
    } catch { toast.error('Failed to load map data'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadData(); }, [loadData]);

  const pullExternal = async () => {
    setPulling(true);
    try {
      const res = await api.post('/api/map/pull-external');
      toast.success(res.data.message);
      loadData();
    } catch { toast.error('Pull failed'); }
    finally { setPulling(false); }
  };

  const handleSelectComplete = useCallback((bounds) => {
    setSelBounds(bounds);
    const inside = pins.filter(p => bounds.contains([p.lat, p.lng]));
    setSelected(inside);
    setSidebarTab('selected');
    if (inside.length > 0) toast.success(`${inside.length} properties selected`);
    else toast.info('No properties in selected area');
    setMode('click');
  }, [pins]);

  const clearSelection = () => { setSelected([]); setSelBounds(null); setSidebarTab('browse'); };

  const filteredPins = pins.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.name||'').toLowerCase().includes(q) || (p.village||'').toLowerCase().includes(q) || (p.address||'').toLowerCase().includes(q);
  });

  const tileLayers = {
    satellite: { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attr: 'Esri World Imagery' },
    street:    { url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', attr: '© OpenStreetMap contributors © CARTO' },
  };

  const SB = { padding: '0 14px', fontSize: 12 };

  return (
    <div style={{ position:'relative', height:'calc(100vh - 60px)', display:'flex', overflow:'hidden', background:'#111' }}>

      {/* ── Left Sidebar ── */}
      <div style={{ width:300, flexShrink:0, display:'flex', flexDirection:'column', background:'#fff', borderRight:'1px solid #e5e7eb', zIndex:10 }}>

        {/* Search */}
        <div style={{ padding:'12px 12px 8px', borderBottom:'1px solid #e5e7eb' }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, fontWeight:700, marginBottom:6, color:'#0D2A4A' }}>
            Hamptons XP Map
          </div>
          <div style={{ position:'relative', marginBottom:8 }}>
            <span style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', fontSize:13 }}>🔍</span>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search properties..."
              style={{ width:'100%', paddingLeft:28, paddingRight:8, paddingTop:7, paddingBottom:7, border:'1px solid #D1D5DB', borderRadius:8, fontSize:13, boxSizing:'border-box', outline:'none' }}
            />
          </div>

          {/* Layer toggles */}
          <div style={{ display:'flex', gap:5, marginBottom:6 }}>
            {[{k:'restaurants',i:'🍽',c:'#D97706',l:'Restaurants'},{k:'spaces',i:'🏢',c:'#0D9488',l:'Spaces'},{k:'contacts',i:'👤',c:'#2563EB',l:'Contacts'}].map(layer=>(
              <button key={layer.k} onClick={()=>setFilters(f=>({...f,[layer.k]:!f[layer.k]}))}
                style={{ flex:1, padding:'5px 2px', borderRadius:7, fontSize:10, fontWeight:700, cursor:'pointer', textAlign:'center', lineHeight:1.4,
                  border:`2px solid ${filters[layer.k]?layer.c:'#D1D5DB'}`,
                  background:filters[layer.k]?`${layer.c}18`:'transparent',
                  color:filters[layer.k]?layer.c:'#6B7280' }}>
                {layer.i}<br/>{layer.l.slice(0,6)}<br/><span style={{fontWeight:400}}>({filteredPins.filter(p=>p.type===layer.k).length})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ display:'flex', borderBottom:'1px solid #e5e7eb' }}>
          {[['browse','Browse'],['selected',`Selected (${selected.length})`]].map(([t,l])=>(
            <button key={t} onClick={()=>setSidebarTab(t)}
              style={{ flex:1, padding:'9px 0', fontSize:12, fontWeight:600, cursor:'pointer', background:'none', border:'none',
                borderBottom:sidebarTab===t?'2px solid #0D2A4A':'2px solid transparent',
                color:sidebarTab===t?'#0D2A4A':'#6B7280' }}>
              {l}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {sidebarTab === 'selected' ? (
            <div>
              {selected.length === 0 ? (
                <div style={{ padding:24, textAlign:'center', color:'#6B7280' }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>◻️</div>
                  <div style={{ fontSize:13, fontWeight:600 }}>No area selected</div>
                  <div style={{ fontSize:12, marginTop:4 }}>Use the Select tool to draw an area on the map</div>
                </div>
              ) : (
                <div>
                  <div style={{ padding:'10px 14px', borderBottom:'1px solid #e5e7eb', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <span style={{ fontSize:13, fontWeight:700, color:'#0D2A4A' }}>📌 {selected.length} Properties</span>
                    <button onClick={clearSelection} style={{ fontSize:11, color:'#6B7280', cursor:'pointer', background:'none', border:'1px solid #D1D5DB', borderRadius:5, padding:'2px 8px' }}>Clear</button>
                  </div>
                  {selected.map(pin => (
                    <div key={pin.id} style={{ padding:'10px 14px', borderBottom:'1px solid #F3F4F6', cursor:'pointer' }}
                      onClick={() => setFlyTarget({ coords: [pin.lat, pin.lng], zoom: 16 })}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:10, height:10, borderRadius:'50%', background: TYPE_COLOR[pin.type], flexShrink:0 }} />
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:12, fontWeight:700, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{pin.name}</div>
                          {pin.address && <div style={{ fontSize:11, color:'#6B7280' }}>{pin.address}</div>}
                          <div style={{ display:'flex', gap:12, marginTop:3 }}>
                            {pin.type==='restaurant'&&pin.monthly_revenue&&<span style={{fontSize:11,color:'#D97706',fontWeight:600}}>{fmt(pin.monthly_revenue)}/mo</span>}
                            {pin.type==='space'&&pin.rent_monthly&&<span style={{fontSize:11,color:'#0D9488',fontWeight:600}}>{fmt(pin.rent_monthly)}/mo</span>}
                            {pin.type==='space'&&pin.size_sqft&&<span style={{fontSize:11,color:'#6B7280'}}>{pin.size_sqft.toLocaleString()} sqft</span>}
                            {pin.phone&&<span style={{fontSize:11,color:'#2563EB'}}>{pin.phone}</span>}
                          </div>
                        </div>
                        <span style={{ fontSize:11, color:'#6B7280' }}>{pin.village}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              {/* Village jump list */}
              <div style={{ padding:'8px 14px 4px', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'#9CA3AF' }}>Jump to Village</div>
              {Object.entries(summary).map(([village, data]) => {
                const tot = (data.restaurants||0)+(data.spaces||0)+(data.contacts||0);
                if (!tot) return null;
                return (
                  <button key={village} onClick={()=>setFlyTarget({coords:data.coords,zoom:14})}
                    style={{ width:'100%', display:'flex', justifyContent:'space-between', padding:'7px 14px', background:'none', border:'none', cursor:'pointer', fontSize:13, color:'#111827', borderBottom:'1px solid #F9FAFB' }}>
                    <span style={{ fontWeight:500 }}>{village}</span>
                    <span style={{ fontSize:11, color:'#6B7280' }}>
                      {data.restaurants>0&&`🍽${data.restaurants} `}{data.spaces>0&&`🏢${data.spaces} `}{data.contacts>0&&`👤${data.contacts}`}
                    </span>
                  </button>
                );
              })}

              <div style={{ padding:'10px 14px 4px', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'#9CA3AF', marginTop:4 }}>
                {search ? `Results (${filteredPins.length})` : `All Items (${filteredPins.length})`}
              </div>
              {filteredPins.slice(0,40).map(pin => (
                <button key={pin.id} onClick={()=>setFlyTarget({coords:[pin.lat,pin.lng],zoom:16})}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'7px 14px', background:'none', border:'none', borderBottom:'1px solid #F9FAFB', cursor:'pointer', textAlign:'left' }}>
                  <span style={{ fontSize:14 }}>{TYPE_ICON[pin.type]}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'#111827' }}>{pin.name}</div>
                    <div style={{ fontSize:11, color:'#6B7280' }}>
                      {pin.village}
                      {pin.type==='restaurant'&&pin.monthly_revenue?` · ${fmt(pin.monthly_revenue)}/mo`:''}
                      {pin.type==='space'&&pin.rent_monthly?` · ${fmt(pin.rent_monthly)}/mo`:''}
                    </div>
                  </div>
                </button>
              ))}
              {filteredPins.length > 40 && <div style={{ padding:'8px 14px', fontSize:11, color:'#9CA3AF', textAlign:'center' }}>+{filteredPins.length-40} more — use search to filter</div>}
            </div>
          )}
        </div>

        {/* Pull from Zillow/Google Maps */}
        <div style={{ padding:12, borderTop:'1px solid #e5e7eb' }}>
          <button onClick={pullExternal} disabled={pulling}
            style={{ width:'100%', padding:'9px 0', background:'#0D2A4A', color:'white', border:'none', borderRadius:8, fontSize:13, fontWeight:700, cursor:pulling?'default':'pointer', opacity:pulling?.7:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
            {pulling ? <><div style={{ width:14, height:14, border:'2px solid white', borderTopColor:'transparent', borderRadius:'50%', animation:'spin .8s linear infinite' }} />Pulling data...</> : '🔍 Pull from Zillow & Google Maps'}
          </button>
          <div style={{ fontSize:10, color:'#9CA3AF', textAlign:'center', marginTop:4 }}>Adds new restaurants & RE prices to CRM</div>
        </div>
      </div>

      {/* ── Map area ── */}
      <div style={{ flex:1, position:'relative' }}>

        {/* Toolbar */}
        <div style={{ position:'absolute', top:12, left:'50%', transform:'translateX(-50%)', zIndex:1000, display:'flex', background:'white', borderRadius:10, boxShadow:'0 2px 14px rgba(0,0,0,.2)', overflow:'hidden', border:'1px solid #E5E7EB' }}>
          {[
            { id:'click',   label:'Click',   icon:'↖' },
            { id:'select',  label:'Select',  icon:'⬜' },
          ].map(tool => (
            <button key={tool.id} onClick={()=>{ setMode(tool.id); if(tool.id==='click') clearSelection(); }}
              style={{ padding:'8px 18px', fontSize:13, fontWeight:600, cursor:'pointer', background:mode===tool.id?'#0D2A4A':'transparent', color:mode===tool.id?'white':'#374151', border:'none', display:'flex', alignItems:'center', gap:5 }}>
              <span>{tool.icon}</span>{tool.label}
            </button>
          ))}
          <div style={{ width:1, background:'#E5E7EB', margin:'6px 0' }} />
          <button onClick={()=>setMapStyle(s=>s==='satellite'?'street':'satellite')}
            style={{ padding:'8px 14px', fontSize:13, fontWeight:600, cursor:'pointer', background:'transparent', color:'#374151', border:'none', display:'flex', alignItems:'center', gap:5 }}>
            {mapStyle==='satellite'?'🗺 Street':'🛰 Satellite'}
          </button>
          {selected.length > 0 && (
            <button onClick={clearSelection}
              style={{ padding:'8px 14px', fontSize:13, fontWeight:600, cursor:'pointer', background:'#FEE2E2', color:'#991B1B', border:'none' }}>
              ✕ Clear ({selected.length})
            </button>
          )}
        </div>

        {mode === 'select' && (
          <div style={{ position:'absolute', top:60, left:'50%', transform:'translateX(-50%)', zIndex:1000, background:'#0891b2', color:'white', padding:'6px 16px', borderRadius:20, fontSize:12, fontWeight:600 }}>
            ⬜ Click &amp; drag to select an area
          </div>
        )}

        {loading && (
          <div style={{ position:'absolute', top:60, right:16, zIndex:1000, background:'white', borderRadius:20, padding:'6px 14px', boxShadow:'0 2px 8px rgba(0,0,0,.15)', fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
            <div style={{ width:12, height:12, border:'2px solid #0D2A4A', borderTopColor:'transparent', borderRadius:'50%', animation:'spin .8s linear infinite' }} /> Loading map...
          </div>
        )}

        <MapContainer center={HAMPTONS_CENTER} zoom={11} minZoom={9} maxZoom={19} zoomControl={false}
          style={{ height:'100%', width:'100%' }}>
          <TileLayer key={mapStyle} url={tileLayers[mapStyle].url} attribution={tileLayers[mapStyle].attr} maxZoom={19} />
          <ZoomControl position="bottomright" />
          <FlyTo target={flyTarget} />
          <SelectionTool mode={mode} onSelectComplete={handleSelectComplete} />

          {filteredPins.map(pin => {
            const isSelected = selected.some(s => s.id === pin.id);
            const icon = makePin(TYPE_COLOR[pin.type] || '#374151', isSelected);
            return (
              <Marker key={pin.id} position={[pin.lat, pin.lng]} icon={icon}>
                <Popup minWidth={230} maxWidth={290}>
                  <PinCard pin={pin} />
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        {/* Legend */}
        <div style={{ position:'absolute', bottom:32, right:16, zIndex:1000, background:'rgba(255,255,255,.95)', backdropFilter:'blur(4px)', borderRadius:10, padding:'10px 14px', boxShadow:'0 2px 12px rgba(0,0,0,.15)', fontSize:12 }}>
          <div style={{ fontWeight:700, marginBottom:6, color:'#0D2A4A', fontFamily:'Cormorant Garamond,serif', fontSize:13 }}>Legend</div>
          {[['🍽','#D97706',`Restaurants (${filteredPins.filter(p=>p.type==='restaurant').length})`],
            ['🏢','#0D9488',`Spaces (${filteredPins.filter(p=>p.type==='space').length})`],
            ['👤','#2563EB',`Contacts (${filteredPins.filter(p=>p.type==='contact').length})`]].map(([i,c,l])=>(
            <div key={l} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
              <span>{i}</span><span style={{ color:'#374151' }}>{l}</span>
            </div>
          ))}
          <div style={{ marginTop:6, fontSize:10, color:'#9CA3AF' }}>
            {filteredPins.length.toLocaleString()} total pins
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .leaflet-popup-content { margin: 8px 12px; }
        .leaflet-popup-content-wrapper { border-radius: 10px !important; padding: 0 !important; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,.2) !important; }
      `}</style>
    </div>
  );
}
